import { NavigationGuardNext, Route } from 'vue-router'

import store from '@/store'
import adminNavItems from '@/navigation/vertical/admin/index'
import { getRoutePermission } from '@/constants/authorization/route-permissions'
import {
  getRequiredPermissionsFromRoute,
  getRequiredUserTypesFromRoute,
  resolveSubuserUnauthorizedRedirect,
  isSubuserHasPermissions,
  isUserHasTypes,
} from '@/utils/permissions-helper'

const SUBUSER_EXEMPT_ADMIN_ROUTES = new Set([
  'admin:home',
  'admin:profile',
  'admin:welcome',
  'admin:shortcuts',
])

function getUnauthorizedFallbackRoute(to: Route) {
  const hasAnySubuserPermission = store.getters['permissions/hasAnySubuserPermission']
  return resolveSubuserUnauthorizedRedirect(to.name, hasAnySubuserPermission, adminNavItems as any[])
}

async function checkUserTypeGuard(to: Route, from: Route, next: NavigationGuardNext) {
  if (!store.getters['auth/isAuthenticated']) {
    return next()
  }

  if (!store.getters['permissions/isPrimaryAccount']) {
    const onlyOwner = to.matched.some(record => record.meta?.onlyOwner)
    if (onlyOwner) {
      return next(getUnauthorizedFallbackRoute(to))
    }

    const requiredPermissions = getRequiredPermissionsFromRoute(to)
    if (requiredPermissions?.length) {
      const hasPermission = store.getters['permissions/hasSubuserPermission']
      if (!isSubuserHasPermissions(requiredPermissions, hasPermission)) {
        const fallback = getUnauthorizedFallbackRoute(to)
        if (fallback.name === to.name) {
          return next({ name: 'unauthorized' })
        }
        return next(fallback)
      }
    } else if (
      to.name?.startsWith('admin:')
      && !SUBUSER_EXEMPT_ADMIN_ROUTES.has(to.name)
      && !getRoutePermission(to.name)
    ) {
      const fallback = getUnauthorizedFallbackRoute(to)
      if (fallback.name === to.name) {
        return next({ name: 'unauthorized' })
      }
      return next(fallback)
    }

    return next()
  }

  const requiredTypes: Array<Array<string>> = getRequiredUserTypesFromRoute(to)
  const isUserHasType = store.getters['permissions/isUserHasType']
  if (requiredTypes?.length && !isUserHasTypes(requiredTypes, isUserHasType)) {
    return next({ name: 'unauthorized' })
  }

  return next()
}

export default checkUserTypeGuard
